import axios from "./httpClient";
import { getApiBaseUrl } from "../data/authStorage";

const apiBaseUrl = getApiBaseUrl();

const QUERY_STATUS_TO_UI = {
  OPEN: "Pending",
  IN_PROGRESS: "In Progress",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
};

const QUERY_STATUS_TO_API = {
  Pending: "OPEN",
  Active: "IN_PROGRESS",
  "In Progress": "IN_PROGRESS",
  Answered: "RESOLVED",
  Resolved: "RESOLVED",
  Closed: "CLOSED",
};

const QUERY_PRIORITY_TO_UI = {
  LOW: "Low",
  MEDIUM: "Medium",
  HIGH: "High",
  URGENT: "Critical",
  CRITICAL: "Critical",
};

const QUERY_PRIORITY_TO_API = {
  Low: "LOW",
  Medium: "MEDIUM",
  High: "HIGH",
  Critical: "URGENT",
};

const getArrayPayload = (payload) =>
  payload?.queries || payload?.data || (Array.isArray(payload) ? payload : []);

const normalizeAdminQuery = (query = {}) => ({
  ...query,
  title: query.title || query.subject || "",
  subject: query.subject || query.title || "",
  status: QUERY_STATUS_TO_UI[query.status] || query.status || "Pending",
  rawStatus: query.status,
  priority: QUERY_PRIORITY_TO_UI[query.priority] || query.priority || "Medium",
  rawPriority: query.priority,
  disciplinaryType: query.disciplinaryType || "QUERY",
});

const normalizeEmployeeAssignment = (assignment = {}) => {
  const query = assignment.query || assignment;

  return {
    ...query,
    assignmentId: assignment.assignmentId || (assignment.query ? assignment.id : undefined),
    id: query.id || assignment.queryId || assignment.id,
    queryId: query.id || assignment.queryId || assignment.id,
    title: query.title || query.subject || "",
    subject: query.subject || query.title || "",
    status: QUERY_STATUS_TO_UI[query.status] || query.status || "Pending",
    rawStatus: query.status,
    priority: QUERY_PRIORITY_TO_UI[query.priority] || query.priority || "Medium",
    rawPriority: query.priority,
    disciplinaryType: query.disciplinaryType || "QUERY",
    servedAt: assignment.assignedAt || query.servedAt || query.createdAt,
    deadline: assignment.deadline,
    isRead: assignment.isRead ?? query.isRead,
    hasResponded: assignment.hasResponded ?? query.hasResponded,
    respondedAt: assignment.respondedAt || query.respondedAt,
    responses: query.responses || [],
  };
};

const toApiFilters = (filters = {}) => ({
  ...filters,
  status: QUERY_STATUS_TO_API[filters.status] || filters.status,
  priority: QUERY_PRIORITY_TO_API[filters.priority] || filters.priority,
});

const toCreatePayload = (queryData = {}) => ({
  ...queryData,
  subject: queryData.subject || queryData.title,
  priority: QUERY_PRIORITY_TO_API[queryData.priority] || queryData.priority,
});

// Admin - Create and serve queries
export const createQuery = async (queryData) => {
  try {
    const response = await axios.post(`${apiBaseUrl}/queries`, toCreatePayload(queryData));
    return normalizeAdminQuery(response.data);
  } catch (error) {
    throw error;
  }
};

export const createAttendanceQuery = async (payload) => {
  const response = await axios.post(`${apiBaseUrl}/queries/attendance-prompt`, payload);
  return response.data;
};

// Admin - Get all queries (admin view)
export const getAllQueries = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    const apiFilters = toApiFilters(filters);
    if (apiFilters.status) params.append("status", apiFilters.status);
    if (apiFilters.priority) params.append("priority", apiFilters.priority);
    if (filters.category) params.append("category", filters.category);
    if (filters.disciplinaryType) params.append("disciplinaryType", filters.disciplinaryType);
    params.append("limit", "100");
    
    const response = await axios.get(
      `${apiBaseUrl}/queries${params.toString() ? "?" + params.toString() : ""}`
    );
    const queries = getArrayPayload(response.data).map(normalizeAdminQuery);
    return { ...response.data, data: queries, queries };
  } catch (error) {
    throw error;
  }
};

// Admin - Get single query detail
export const getQueryDetails = async (queryId) => {
  try {
    const response = await axios.get(`${apiBaseUrl}/queries/${queryId}`);
    return normalizeAdminQuery(response.data);
  } catch (error) {
    throw error;
  }
};

// Admin - Serve query to employees
export const serveQueryToEmployees = async (queryId, employeeIds) => {
  try {
    const response = await axios.post(
      `${apiBaseUrl}/queries/${queryId}/serve`,
      { employeeIds }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Admin - Update query status
export const updateQueryStatus = async (queryId, status) => {
  try {
    const response = await axios.patch(`${apiBaseUrl}/queries/${queryId}/status`, {
      status: QUERY_STATUS_TO_API[status] || status,
    });
    return normalizeAdminQuery(response.data);
  } catch (error) {
    throw error;
  }
};

// Admin - Delete query
export const deleteQuery = async (queryId) => {
  try {
    const response = await axios.delete(`${apiBaseUrl}/queries/${queryId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Employee - Get assigned queries (queries served to them)
export const getEmployeeQueries = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    const apiFilters = toApiFilters(filters);
    if (apiFilters.status) params.append("status", apiFilters.status);
    if (apiFilters.priority) params.append("priority", apiFilters.priority);
    
    const response = await axios.get(
      `${apiBaseUrl}/queries/employee/my-queries${params.toString() ? "?" + params.toString() : ""}`
    );
    const queries = getArrayPayload(response.data).map(normalizeEmployeeAssignment);
    return { ...response.data, data: queries, queries };
  } catch (error) {
    throw error;
  }
};

// Employee - Get single assigned query details
export const getEmployeeQueryDetails = async (queryId) => {
  try {
    const response = await axios.get(`${apiBaseUrl}/queries/employee/${queryId}`);
    const query = normalizeEmployeeAssignment(response.data?.data || response.data?.query || response.data);
    return { ...response.data, data: query, query };
  } catch (error) {
    throw error;
  }
};

// Employee - Submit response to query
export const submitQueryResponse = async (queryId, response) => {
  try {
    if (!queryId) {
      throw new Error("Query ID is required to submit a response.");
    }

    const result = await axios.post(
      `${apiBaseUrl}/queries/${queryId}/respond`,
      response
    );
    return result.data;
  } catch (error) {
    throw error;
  }
};

// Employee - Mark query assignment as read
export const markQueryAsRead = async (assignmentId) => {
  try {
    const response = await axios.patch(
      `${apiBaseUrl}/queries/assignment/${assignmentId}/mark-read`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Employee - Archive query assignment
export const archiveQueryAssignment = async (assignmentId) => {
  try {
    const response = await axios.patch(
      `${apiBaseUrl}/queries/assignment/${assignmentId}/archive`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
// Get query categories
export const getQueryCategories = async (options = {}) => {
  try {
    const response = await axios.get(`${apiBaseUrl}/queries/categories`, {
      params: options.includeInactive ? { includeInactive: "true" } : undefined,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createQueryCategory = async (categoryData) => {
  try {
    const response = await axios.post(`${apiBaseUrl}/queries/categories`, categoryData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateQueryCategory = async (categoryId, categoryData) => {
  try {
    const response = await axios.patch(`${apiBaseUrl}/queries/categories/${categoryId}`, categoryData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteQueryCategory = async (categoryId) => {
  try {
    const response = await axios.delete(`${apiBaseUrl}/queries/categories/${categoryId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Export query data
export const exportQueries = async (format = "csv") => {
  try {
    const response = await axios.get(`${apiBaseUrl}/queries/export`, {
      params: { format },
      responseType: format === "csv" ? "blob" : "json",
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};
